package com.klantenstop.web.components.moderation

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.table.DataTable
import com.klantenstop.web.components.table.TableColumnDef
import com.klantenstop.web.data.models.domain.ModerationEntry
import com.klantenstop.web.data.models.dto.responses.moderation.ModerationActionType
import com.klantenstop.web.utils.maxLines
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Text

/**
 * Specialized moderation table with predefined columns
 *
 * @param items List of moderation entries to display
 * @param emptyMessage Message to show when list is empty
 * @param expandedRowId ID of the currently expanded row (null if no expansion)
 * @param onRowClick Callback when a row is clicked (for expansion support)
 * @param expandedContent Composable to render in the expanded row (receives the entry)
 */
@Composable
fun ModerationTable(
    items: List<ModerationEntry>,
    emptyMessage: String = "Geen items in de wachtrij.",
    expandedRowId: String? = null,
    onRowClick: ((ModerationEntry) -> Unit)? = null,
    expandedContent: (@Composable (ModerationEntry) -> Unit)? = null
) {
    DataTable(
        items = items,
        columns = MODERATION_COLUMNS,
        emptyMessage = emptyMessage,
        expandedRowKey = expandedRowId,
        getRowKey = { it.id },
        onRowClick = onRowClick,
        expandedContent = expandedContent
    )
}

/**
 * Standard column definitions for moderation tables
 */
private const val DELETED_USER_LABEL = "[verwijderd]"

private val MODERATION_COLUMNS = listOf(
    TableColumnDef<ModerationEntry>(
        heading = "Actie",
        resizable = true,
        wrapContent = true,
        comparator = compareBy { it.actionType.displayLabel() },
        cellContent = { entry ->
            Text(entry.actionType.displayLabel())
        }
    ),
    TableColumnDef(
        heading = "Naam",
        resizable = true,
        comparator = compareBy { it.entityName ?: it.entityId },
        cellContent = { entry ->
            SpanText(
                modifier = Modifier.maxLines(1),
                text = entry.entityName ?: entry.entityId
            )
        }
    ),
    TableColumnDef(
        heading = "Ingediend door",
        resizable = true,
        wrapContent = true,
        comparator = compareBy { it.submitterName ?: it.submittedByUserId ?: DELETED_USER_LABEL },
        cellContent = { entry ->
            SpanText(
                modifier = Modifier.maxLines(1),
                text = entry.submitterName ?: entry.submittedByUserId ?: DELETED_USER_LABEL
            )
        }
    ),
    TableColumnDef(
        heading = "Datum",
        resizable = false,
        wrapContent = true,
        comparator = compareBy { it.createdAt },
        cellContent = { entry ->
            Text(entry.createdAt.substringBefore("T"))
        }
    )
)

/**
 * Display label for moderation action types
 */
fun ModerationActionType.displayLabel() = when (this) {
    ModerationActionType.CREATE -> "Aanmaken"
    ModerationActionType.UPDATE -> "Wijzigen"
}
